import { useEffect, useState } from 'react'

export default function AdminProductGroups() {
  const [groups, setGroups]       = useState([])
  const [selected, setSelected]   = useState(null)
  const [form, setForm]           = useState(null)
  const [mode, setMode]           = useState('view')   // view | new | edit
  const [error, setError]         = useState('')
  const [success, setSuccess]     = useState('')

  const API = process.env.REACT_APP_API_URL

  useEffect(() => {
    fetchGroups()
  }, [])

  const fetchGroups = async () => {
    try {
      const res = await fetch(`${API}/product-groups`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      if (!res.ok) throw new Error()
      setGroups(await res.json())
    } catch {
      setError('Kunne ikke hente grupper')
    }
  }

  const handleNew = () => {
    setForm({ name: '', description: '' })
    setSelected(null)
    setMode('new')
    setError(''); setSuccess('')
  }

  const handleEdit = () => {
    setForm({ name: selected.name, description: selected.description || '' })
    setMode('edit')
    setError(''); setSuccess('')
  }

  const handleCancel = () => {
    setForm(null)
    setMode('view')
    setError(''); setSuccess('')
  }

  const handleChange = e => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setError(''); setSuccess('')

    const url    = mode === 'edit'
      ? `${API}/product-groups/${selected.id}`
      : `${API}/product-groups`
    const method = mode === 'edit' ? 'PUT' : 'POST'

    try {
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(form)
      })
      const body = await res.json()
      if (!res.ok) throw new Error(body.error || 'Feil ved lagring')

      setSuccess(mode === 'edit' ? 'Oppdatert!' : 'Opprettet!')
      setMode('view')
      setForm(null)
      setSelected(null)
      fetchGroups()
    } catch (err) {
      setError(err.message)
    }
  }

  const handleDelete = async () => {
    if (!selected) return
    if (!window.confirm(`Slett gruppe «${selected.name}»?`)) return

    try {
      const res = await fetch(`${API}/product-groups/${selected.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      if (!res.ok) throw new Error('Feil ved sletting')
      setSuccess('Gruppe slettet')
      setSelected(null)
      fetchGroups()
    } catch (err) {
      setError(err.message)
    }
  }

  return (
    <div className="w-full min-h-[400px] flex gap-6">
      {/* VENSTRE: Liste */}
      <div className="w-1/2 pr-6 border-r">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-[#000099]">Grupper</h2>
          <button
            onClick={handleNew}
            className="bg-[#000099] text-white px-4 py-1 rounded hover:bg-blue-800 transition"
          >
            + Ny
          </button>
        </div>
        {error && <p className="text-red-500 mb-3">{error}</p>}
        <ul className="space-y-2">
          {groups.map(g => (
            <li
              key={g.id}
              onClick={() => { setSelected(g); setMode('view'); setForm(null); setError(''); setSuccess('') }}
              className="p-3 border border-gray-200 rounded hover:bg-gray-50 cursor-pointer"
            >
              {g.name}
            </li>
          ))}
        </ul>
      </div>

      {/* HØYRE: Detaljer / Skjema */}
      <div className="w-1/2 pl-6">
        <h2 className="text-2xl font-bold text-[#000099] mb-4">
          {mode === 'new'
            ? 'Ny gruppe'
            : mode === 'edit'
            ? 'Rediger gruppe'
            : 'Detaljer'}
        </h2>

        {success && <p className="text-green-600 mb-3">{success}</p>}

        {(mode === 'new' || mode === 'edit') && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Navn */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Navn <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={form.name || ''}
                onChange={handleChange}
                className="mt-1 w-full border border-gray-300 px-3 py-2 rounded focus:ring focus:ring-[#000099]"
                required
              />
            </div>

            {/* Beskrivelse */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Beskrivelse
              </label>
              <textarea
                id="description"
                name="description"
                rows={3}
                value={form.description || ''}
                onChange={handleChange}
                className="mt-1 w-full border border-gray-300 px-3 py-2 rounded focus:ring focus:ring-[#000099]"
              />
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 transition"
              >
                Lagre
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="bg-gray-300 text-gray-800 px-6 py-2 rounded hover:bg-gray-400 transition"
              >
                Avbryt
              </button>
            </div>
          </form>
        )}

        {/* Vis / Rediger / Slett */}
        {mode === 'view' && selected && (
          <div className="space-y-2 text-gray-800">
            <p><strong>Navn:</strong> {selected.name}</p>
            <p><strong>Beskrivelse:</strong> {selected.description || '-'}</p>
            <div className="flex gap-4 pt-4">
              <button
                onClick={handleEdit}
                className="bg-yellow-500 text-white px-6 py-2 rounded hover:bg-yellow-600 transition"
              >
                Rediger
              </button>
              <button
                onClick={handleDelete}
                className="bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700 transition"
              >
                Slett
              </button>
            </div>
          </div>
        )}

        {mode === 'view' && !selected && (
          <p className="text-gray-600">Velg en gruppe for detaljer.</p>
        )}
      </div>
    </div>
)
}
